/**
 * PharmacyManager Component
 *
 * Manages patient pharmacies - replicating the Streamlit app flow exactly.
 * Features:
 * - View current patient pharmacies with preferred indicator
 * - Add new pharmacies via search
 * - Set preferred pharmacy
 * - Remove pharmacies
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  LocalPharmacy as PharmacyIcon,
  Phone as PhoneIcon,
  Fax as FaxIcon,
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { ElationPushDialog } from '../elation';
import { PatientService } from '../../services/patient.service';
import { Pharmacy, PatientPharmacy, formatPhone } from '../../model/provider.types';
import { toast } from 'react-toastify';
import Helper from '../../utils/helper';

const patientService = new PatientService();


interface PharmacyManagerProps {
  patientId: number | string;
  open: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export const PharmacyManager: React.FC<PharmacyManagerProps> = ({
  patientId,
  open,
  onClose,
  onUpdate,
}) => {
  // Patient pharmacies state
  const [pharmacies, setPharmacies] = useState<PatientPharmacy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add pharmacy dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Pharmacy search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchResults, setSearchResults] = useState<Pharmacy[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState<Pharmacy | null>(null);
  const [saving, setSaving] = useState(false);

  // Elation push dialog state
  const [elationPushOpen, setElationPushOpen] = useState(false);

  // Load patient pharmacies
  const loadPharmacies = useCallback(async () => {
    if (!patientId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await patientService.getPatientPharmacies(patientId);
      setPharmacies(response.data?.pharmacies || []);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load pharmacies');
      setPharmacies([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (open && patientId) {
      loadPharmacies();
    }
  }, [open, patientId, loadPharmacies]);

  // Search pharmacies
  const handleSearch = async () => {
    if (!searchQuery.trim() && !searchCity.trim() && !searchState) {
      toast.warning('Please enter a search term', Helper.bottom_center());
      return;
    }

    try {
      setSearching(true);
      setSearchResults([]);
      setSelectedPharmacy(null);

      const response = await patientService.searchPharmacies({
        query: searchQuery.trim() || undefined,
        city: searchCity.trim() || undefined,
        state: searchState || undefined,
        limit: 50,
      });

      setSearchResults(response.data?.pharmacies || []);
    } catch (err: any) {
      toast.error('Pharmacy search failed', Helper.bottom_center());
    } finally {
      setSearching(false);
    }
  };

  // Check if pharmacy is already assigned to patient
  const isPharmacyAssigned = (pharmacyId: number): boolean => {
    return pharmacies.some(pp => pp.pharmacy_id === pharmacyId);
  };

  // Add pharmacy to patient
  const handleAddPharmacy = async () => {
    if (!selectedPharmacy) {
      toast.error('Please select a pharmacy', Helper.bottom_center());
      return;
    }

    try {
      setSaving(true);

      await patientService.addPatientPharmacy(patientId, {
        pharmacy_id: selectedPharmacy.id,
        source: 'manual',
      });

      toast.success('Pharmacy added', Helper.bottom_center());
      handleCloseAddDialog();
      loadPharmacies();
      onUpdate?.();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error?.message || 'Failed to add pharmacy';
      toast.error(errorMsg, Helper.bottom_center());
    } finally {
      setSaving(false);
    }
  };

  // Set as preferred pharmacy
  const handleSetPreferred = async (patientPharmacy: PatientPharmacy) => {
    try {
      await patientService.setPreferredPharmacy(patientId, patientPharmacy.id);
      toast.success('Preferred pharmacy updated', Helper.bottom_center());
      loadPharmacies();
      onUpdate?.();
    } catch (err: any) {
      toast.error('Failed to update preferred pharmacy', Helper.bottom_center());
    }
  };

  // Remove pharmacy
  const handleRemovePharmacy = async (patientPharmacy: PatientPharmacy) => {
    const pharmacyName = patientPharmacy.pharmacy?.store_name || 'this pharmacy';
    if (!window.confirm(`Remove ${pharmacyName} from patient's pharmacies?`)) {
      return;
    }

    try {
      await patientService.removePatientPharmacy(patientId, patientPharmacy.id);
      toast.success('Pharmacy removed', Helper.bottom_center());
      loadPharmacies();
      onUpdate?.();
    } catch (err: any) {
      toast.error('Failed to remove pharmacy', Helper.bottom_center());
    }
  };

  // Close add dialog and reset state
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSearchQuery('');
    setSearchCity('');
    setSearchState('');
    setSearchResults([]);
    setSelectedPharmacy(null);
  };

  // Render a pharmacy card
  const renderPharmacyCard = (patientPharmacy: PatientPharmacy) => {
    const pharmacy = patientPharmacy.pharmacy;
    if (!pharmacy) return null;

    return (
      <Card key={patientPharmacy.id} variant="outlined" sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PharmacyIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight={500}>
                  {patientPharmacy.is_preferred && (
                    <StarIcon fontSize="small" sx={{ color: 'gold', verticalAlign: 'middle', mr: 0.5 }} />
                  )}
                  {pharmacy.store_name}
                </Typography>
                {patientPharmacy.is_preferred && (
                  <Chip
                    label="Preferred"
                    size="small"
                    color="primary"
                    sx={{ height: 20 }}
                  />
                )}
              </Stack>

              {/* Address */}
              <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                {pharmacy.full_address || [
                  pharmacy.address_1,
                  pharmacy.city,
                  pharmacy.state,
                  pharmacy.postal_code
                ].filter(Boolean).join(', ')}
              </Typography>

              {/* Phone and Fax */}
              <Stack direction="row" spacing={2} mt={0.5}>
                {pharmacy.phone && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PhoneIcon sx={{ fontSize: 14 }} color="action" />
                    <Typography variant="caption" color="textSecondary">
                      {formatPhone(pharmacy.phone)}
                    </Typography>
                  </Stack>
                )}
                {pharmacy.fax && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <FaxIcon sx={{ fontSize: 14 }} color="action" />
                    <Typography variant="caption" color="textSecondary" fontWeight={500}>
                      {formatPhone(pharmacy.fax)}
                    </Typography>
                  </Stack>
                )}
              </Stack>

              {/* Notes */}
              {patientPharmacy.notes && (
                <Typography variant="caption" color="textSecondary" display="block" mt={0.5} fontStyle="italic">
                  Note: {patientPharmacy.notes}
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={0.5}>
              {!patientPharmacy.is_preferred && (
                <Tooltip title="Set as Preferred">
                  <IconButton size="small" onClick={() => handleSetPreferred(patientPharmacy)}>
                    <StarBorderIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => handleRemovePharmacy(patientPharmacy)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // Render search result pharmacy
  const renderSearchResult = (pharmacy: Pharmacy) => {
    const isAssigned = isPharmacyAssigned(pharmacy.id);
    const isSelected = selectedPharmacy?.id === pharmacy.id;

    return (
      <ListItem
        key={pharmacy.id}
        component="div"
        onClick={() => !isAssigned && setSelectedPharmacy(pharmacy)}
        sx={{
          cursor: isAssigned ? 'default' : 'pointer',
          borderRadius: 1,
          backgroundColor: isSelected ? 'action.selected' : 'transparent',
          opacity: isAssigned ? 0.6 : 1,
          '&:hover': { backgroundColor: isAssigned ? 'transparent' : 'action.hover' },
          mb: 0.5,
        }}
      >
        <ListItemText
          primary={
            <Stack direction="row" spacing={1} alignItems="center">
              <PharmacyIcon fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={500}>
                {pharmacy.store_name}
              </Typography>
              {isAssigned && (
                <Chip label="Assigned" size="small" color="success" sx={{ height: 18 }} />
              )}
            </Stack>
          }
          secondary={
            <Box component="span" sx={{ display: 'block' }}>
              <Typography variant="caption" color="textSecondary" component="span">
                {[pharmacy.address_1, pharmacy.city, pharmacy.state, pharmacy.postal_code]
                  .filter(Boolean).join(', ')}
              </Typography>
              {(pharmacy.phone || pharmacy.fax) && (
                <Typography variant="caption" color="textSecondary" component="span" display="block">
                  {pharmacy.phone && `Ph: ${formatPhone(pharmacy.phone)}`}
                  {pharmacy.phone && pharmacy.fax && ' | '}
                  {pharmacy.fax && <strong>Fax: {formatPhone(pharmacy.fax)}</strong>}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary" component="span" display="block">
                NCPDP: {pharmacy.ncpdp_id}
              </Typography>
            </Box>
          }
        />
      </ListItem>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Manage Pharmacies</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={<CloudUploadIcon />}
                variant="outlined"
                size="small"
                onClick={() => setElationPushOpen(true)}
              >
                Push to Elation
              </Button>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                size="small"
                onClick={() => setAddDialogOpen(true)}
              >
                Add Pharmacy
              </Button>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : pharmacies.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">No pharmacies assigned</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Add First Pharmacy
              </Button>
            </Box>
          ) : (
            <Box>
              {pharmacies.map(renderPharmacyCard)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Pharmacy Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Pharmacy</DialogTitle>
        <DialogContent>
          {/* Search Form — single box, searches name / street / city / state together */}
          <Stack spacing={2} mt={1}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Search pharmacy"
                placeholder="Name, street, city, or state — e.g., CVS Orange City FL"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={searching}
                startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
              >
                Search
              </Button>
            </Stack>
          </Stack>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="textSecondary" gutterBottom>
                {searchResults.length} pharmacies found. Select one to add:
              </Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto', mt: 1 }}>
                <List dense disablePadding>
                  {searchResults.map(renderSearchResult)}
                </List>
              </Box>
            </Box>
          )}

          {/* Selected Pharmacy */}
          {selectedPharmacy && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Selected Pharmacy:
              </Typography>
              <Card variant="outlined">
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedPharmacy.store_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedPharmacy.full_address || [
                      selectedPharmacy.address_1,
                      selectedPharmacy.city,
                      selectedPharmacy.state,
                      selectedPharmacy.postal_code
                    ].filter(Boolean).join(', ')}
                  </Typography>
                  {selectedPharmacy.phone && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Phone: {formatPhone(selectedPharmacy.phone)}
                    </Typography>
                  )}
                  {selectedPharmacy.fax && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      <strong>Fax: {formatPhone(selectedPharmacy.fax)}</strong>
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary" display="block">
                    NCPDP: {selectedPharmacy.ncpdp_id}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddPharmacy}
            disabled={saving || !selectedPharmacy}
          >
            {saving ? <CircularProgress size={20} /> : 'Add Pharmacy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Elation Push Dialog */}
      <ElationPushDialog
        patientId={String(patientId)}
        open={elationPushOpen}
        onClose={() => setElationPushOpen(false)}
      />
    </>
  );
};

export default PharmacyManager;
